import React from 'react';

import AlertSvg from 'src/images/Alert.svg';
import ProactiveGreeting from 'src/images/ProactiveGreeting.svg';
import MinimizedChat from 'src/images/MinimizedChat.svg';
import SubmitArrow from 'src/images/SubmitArrow.svg';
import HelpAppLauncher from 'src/images/HelpAppLauncher.svg';

import ExternalLink from 'src/presentation/components/pages/shared/ExternalLink';
import { CHAT_BRAND } from 'src/constants/branding';

//
// CHAT CONFIGURATIONS PAGE
//

export const LOAD_CHAT_SECTION_DESCRIPTION = (
  <span style={{ textAlign: 'left' }}>
    This is the code you need to run {CHAT_BRAND.name} on your website. Copy and paste this snippet into the head
    section of your website.
  </span>
);

export const INIT_CHAT_SECTION_DESCRIPTION = (
  <span style={{ textAlign: 'left' }}>
    Copy and paste this code into your website to bring up the correct {CHAT_BRAND.name} configuration.
  </span>
);

export const OFFICE_HOURS_MESSAGE_DEFAULT = `<h3>👋 We are here to help. </h3>
<p>
We make it easy for you to contact us on whichever channel you prefer! 
Just like if you were talking to a friend.
</p>
<p style="font-weight: bold !important">Quickest Ways to Reach Us</p>
<p><a href="sms://+14035550185">Send us a text message</a></p>
<p><a href="mailto:test@test.com">Shoot us an email</a></p>
<hr />
<p>Phone Hours 9AM - 6PM EST</p>
<a href="tel:123-456-7890">Call Us</a>
`;

//
// CONFIGURATION SECTION
//

export const CONFIGURATION = 'configuration';
export const WIDGET_TYPE = `${CONFIGURATION}.widgetType`;

export const TYPE = 'type';
export const TEXT = 'text';
export const ICON_TYPE = 'iconType';

export const SDK_CONFIG_DESCRIPTION = (
  <span>
    Customize a {CHAT_BRAND.name} by changing Quick Actions, placement, fonts, and colors. Please note that{' '}
    {CHAT_BRAND.plural} cannot be removed once they are created. Learn more about managing {CHAT_BRAND.plural} in our{' '}
    <ExternalLink to="https://help.gladly.com/docs/create-and-configure-chat">Help Docs</ExternalLink>.
  </span>
);
export const SDK_TEXT_DESCRIPTION = `Select the font and edit the text content for ${CHAT_BRAND.name}.`;
export const MOBILE_SDK_TEXT_DESCRIPTION = 'Assign the Office Hours & Throttle message and input placeholder text';
export const SDK_STYLE_DESCRIPTION = `Styling the ${CHAT_BRAND.name} allows you to change things like border, radius, color and other actions related to visual look and feel.`;
export const MOBILE_SDK_STYLE_DESCRIPTION =
  'Assign the border style, chat bubble color, and optional content such as the Powered by Gladly and privacy disclaimer.';

// WIDGET TYPE
export const SELF_SERVICE_WIDGET_TYPE = 'SELF_SERVICE';
export const CHAT_WIDGET_TYPE = 'CHAT';

export const SELF_SERVICE_WIDGET_AUTOMATION_ENABLED = true;
export const AUTOMATED_MESSAGING_TARGET_TYPE_AGENT = 'AGENT';
export const AUTOMATED_MESSAGING_TARGET_TYPE_AUTOMATION = 'AUTOMATION';

export const WIDGET_TYPE_OPTIONS = Object.freeze({
  [SELF_SERVICE_WIDGET_TYPE]: {
    id: SELF_SERVICE_WIDGET_TYPE,
    label: 'Self-Service',
  },
  [CHAT_WIDGET_TYPE]: {
    id: CHAT_WIDGET_TYPE,
    label: 'No Self-Service',
  },
});

// SECTION RULES - Page types & Exceptions
export const DISPLAY_RULES_PAGES_OPTIONS = {
  ALL_PAGES: {
    id: true,
    label: 'All pages',
  },
  NO_PAGES: {
    id: false,
    label: 'No pages',
  },
};
export const DISPLAY_RULES_EXCEPTIONS_OPTIONS = {
  CONTAINS: {
    id: 'CONTAINS',
    label: 'Contains',
  },
  EXACT: {
    id: 'EXACT',
    label: 'Matches exact',
  },
  REGEX: {
    id: 'REGEX',
    label: 'Matches regex',
  },
};

// QUICK ACTIONS
export const QUICK_ACTION_NAME = 'name';
export const QUICK_ACTION_TYPE = 'content.type';
export const QUICK_ACTION_VALUE = 'content.value';

export const QUICK_ACTIONS_DESCRIPTION = (
  <span>Configure the self-service options that direct customers to Answers or website URLs. </span>
);

export const ACTIONS_DESCRIPTION = (
  <span>Select existing Answers or website URLs you want to display in your {CHAT_BRAND.name}</span>
);

export const QUICK_ACTIONS_CONFIGURATION_DESCRIPTION = (
  <span>
    Action labels are names customers can click on. Copy and paste the Answer ID or website URL for the customer to
    navigate to. Answer IDs can be found in each Answer&apos;s URL via the Answer Admin page. The URL will look like
    https://organization.gladly.com/kb-admin/answer/
    <strong>12345</strong> where <strong>12345</strong> is the Answer ID.
  </span>
);

export const QUICK_ACTIONS_UI_OPTIONS = Object.freeze([
  { label: 'First action', inputPlaceholder: 'Order Status' },
  { label: 'Second action', inputPlaceholder: 'Store Locator' },
  { label: 'Third action', inputPlaceholder: 'Returns and Exchanges' },
  { label: 'Fourth action', inputPlaceholder: 'Repairs' },
]);

export const INTERNAL_ACTION_TYPE = 'INTERNAL';
export const EXTERNAL_ACTION_TYPE = 'EXTERNAL';
export const EMPTY_ANSWER_ID = 'empty-answer';

export const QUICK_ACTION_TYPES = Object.freeze({
  [INTERNAL_ACTION_TYPE]: {
    id: INTERNAL_ACTION_TYPE,
    label: 'Answer',
  },
  [EXTERNAL_ACTION_TYPE]: {
    id: EXTERNAL_ACTION_TYPE,
    label: 'Website URL',
  },
});

export const QUICK_ACTION_VALUE_PLACEHOLDER = Object.freeze({
  [INTERNAL_ACTION_TYPE]: '12345',
  [EXTERNAL_ACTION_TYPE]: 'https://',
});

// ICONS
export const ICON = 'ICON';
export const ICON_TEXT = 'TEXT';

export const CHAT_ICON_TYPE_OPTIONS = Object.freeze({
  [ICON]: {
    id: ICON,
    label: 'Icon',
  },
  [ICON_TEXT]: {
    id: ICON_TEXT,
    label: 'Text',
  },
});

export const X_ICON = 'X_ICON';
export const DASH_ICON = 'DASH_ICON';

export const CLOSE_BUTTON_TYPE_OPTIONS = Object.freeze({
  [X_ICON]: {
    id: X_ICON,
    label: 'X',
  },
  [DASH_ICON]: {
    id: DASH_ICON,
    label: 'Dash',
  },
});

export const GREETING_DESCRIPTION = 'Messages customers see before chatting with an agent.';

export const FIELDS_MAPPER = Object.freeze({
  // CONFIG SECTION

  THROTTLING: {
    path: 'configuration.throttling',
    defaultValue: {
      autoThrottlingEnabled: false,
      autoThrottlingThreshold: 1800,
      percentageRollout: 100,
    },
  },

  ENABLED: {
    path: 'configuration.enabled',
    defaultValue: true,
  },

  DISPLAY: {
    path: 'configuration.display',
    defaultValue: 'ALL_PAGES',
  },

  DISPLAY_RULES_PAGE: {
    path: 'configuration.displayRules.showByDefault',
    defaultValue: true,
    label: 'Default',
    description: `Display ${CHAT_BRAND.name} on`,
  },

  DISPLAY_RULES_EXCEPTIONS: {
    path: 'configuration.displayRules.rules',
    defaultValue: [],
    label: 'a URL',
  },

  WIDGET_TYPE: {
    path: 'configuration.widgetType',
    defaultValue: SELF_SERVICE_WIDGET_TYPE,
    description:
      'Allow customers to search for Answers and use Quick Actions before chatting with an agent. When deselected, customers can only use chat.',
  },

  MESSAGING_AUTOMATION_TARGET_TYPE: {
    path: 'messageAutomationConfiguration.targetType',
    defaultValue: AUTOMATED_MESSAGING_TARGET_TYPE_AGENT,
    description: `When the checkbox is selected, Gladly Sidekick and 3rd party automation services will have access to this ${CHAT_BRAND.name} for the purpose of communication and routing.`,
  },

  OFFICE_HOURS: {
    path: 'configuration.officeHours',
    defaultValue: {
      timezone: 'America/New_York',
      monday: [],
      tuesday: [],
      wednesday: [],
      thursday: [],
      friday: [],
      saturday: [],
      sunday: [],
    },
  },

  OFFICE_HOURS_TIMEZONE: {
    path: 'configuration.officeHours.timezone',
    defaultValue: 'America/New_York',
  },

  SELF_SERVICE_QUICK_ACTIONS: {
    path: 'configuration.selfService.quickActions',
    defaultValue: Array(4).fill({
      name: '',
      content: {
        type: INTERNAL_ACTION_TYPE,
        value: '',
      },
    }),
  },

  INITIAL_CHAT_BUTTON_ICON_TYPE: {
    path: 'configuration.initialChatButton.type',
    defaultValue: ICON,
  },

  INITIAL_CHAT_BUTTON_TEXT: {
    path: 'configuration.initialChatButton.value',
    defaultValue: 'Live chat',
  },

  LANGUAGE: {
    path: 'configuration.lng',
    defaultValue: 'en-us',
    description: 'The language answers customers will be searching on',
  },

  AUDIENCE: {
    path: 'configuration.audienceId',
    description: 'The audience answers customers will be searching on',
    defaultValue: '',
  },

  CLOSE_BUTTON_TYPE: {
    path: 'configuration.closeButton.iconType',
    defaultValue: X_ICON,
    description:
      'Your choice will also be used for Proactive Chat in the proactive greeting. For Proactive Chat, the close icon will appear when the user hovers on the proactive greeting.',
  },

  // TEXT SECTION

  ATTACHMENT_ICON_BUTTON_TITLE: {
    path: 'configuration.customText.chatHistory.attachmentUploadButtonTitle',
    defaultValue: 'Attach file',
  },

  BORDER_WIDTH: {
    path: 'configuration.border.width',
    defaultValue: '1px',
  },

  BORDER_RADIUS: {
    path: 'configuration.borderRadius',
    defaultValue: 'SMALL',
  },

  CLOSE_BUTTON_TITLE: {
    path: 'configuration.closeButton.title',
    defaultValue: 'Close',
  },

  CUSTOM_TEXT_BUTTON_TEXT: {
    path: 'configuration.customText.buttonText',
    defaultValue: 'Chat with us!',
  },

  CUSTOM_TEXT_HEADER_BACK_BUTTON: {
    path: 'configuration.customText.header.backButton',
    defaultValue: 'Back',
  },

  CUSTOM_TEXT_SEARCH_PLACEHOLDER: {
    path: 'configuration.customText.searchPlaceholder',
    defaultValue: 'Search for answers',
  },

  CUSTOM_TEXT_HEADER_GREETING: {
    path: 'configuration.customText.header.greeting',
    defaultValue: 'Let’s chat!',
  },

  CUSTOM_TEXT_HEADER_TITLE: {
    path: 'configuration.customText.header.title',
    defaultValue: 'Hi, how can we help?',
  },

  CUSTOM_TEXT_INACTIVE_BUTTON: {
    path: 'configuration.customText.inactiveButtonText',
    defaultValue: 'Contact us',
  },

  CUSTOM_TEXT_OFFICE_HOURS_MESSAGE: {
    path: 'configuration.customText.officeHourMessage',
    defaultValue: 'Sorry, we are unavailable at this time.',
    description: `Displayed when Customers click on the Chat button outside of ${CHAT_BRAND.name} Hours or when chat support Heroes are unavailable.`,
    mobileDescription: 'Message shown to the customer when chat is unavailable',
  },

  CUSTOM_TEXT_INTERACTIVE_ONBOARDING_EMAIL_PROMPT: {
    path: 'configuration.customText.interactiveOnboarding.emailPrompt',
    defaultValue:
      "Thank you for contacting us.\nWhat's your email? We'll use your email to find your profile and any past conversations you've had with us—not to spam you.",
  },

  CUSTOM_TEXT_FOURTH_MESSAGE: {
    path: 'configuration.customText.interactiveOnboarding.autoResponse',
    defaultValue: 'Got it! Someone will be with you shortly.',
  },

  CUSTOM_TEXT_INTERACTIVE_ONBOARDING_NAME_PROMPT: {
    path: 'configuration.customText.interactiveOnboarding.namePrompt',
    defaultValue: 'Thanks! Can we get your name?',
  },

  CUSTOM_TEXT_COMPOSITION_MESSAGE_PROMPT: {
    path: 'configuration.customText.composition.messagePrompt',
    defaultValue: 'Send message',
  },

  CUSTOM_TEXT_THIRD_MESSAGE: {
    path: 'configuration.customText.interactiveOnboarding.onboardingSuccess',
    defaultValue: 'Hi, <CUSTOMERNAME>! How can we help?',
  },

  DISABLE_AUTO_RESPONSE_MESSAGE: {
    path: 'configuration.onboarding.disableAutoResponseMessage',
    defaultValue: false,
    description: (
      <span>
        When selected, you can send another message before connecting the customer to a support hero. View the{' '}
        <ExternalLink to="https://help.gladly.com/docs/create-and-configure-chat" /> to learn more.
      </span>
    ),
    options: [
      {
        value: true,
        label: 'Disabled',
      },
      {
        value: false,
        label: 'Enabled',
      },
    ],
  },

  FONT_LIBRARY_PRIMARY_FONT_DEFAULT: {
    path: 'configuration.theme.fontLibrary.primary',
    defaultValue: 'Helvetica Neue, Helvetica, sans-serif',
  },

  FONT_LIBRARY_SECONDARY_FONT_DEFAULT: {
    path: 'configuration.theme.fontLibrary.secondary',
    defaultValue: 'Helvetica Neue, Helvetica, sans-serif',
  },

  MINIMIZED_ICON_BUTTON_TITLE: {
    path: 'configuration.minimizedChatButton.title',
    defaultValue: 'Open chat',
  },

  ONBOARDING_COMPOSITION_STAGE: {
    path: 'configuration.onboarding.initialCompositionStage',
    defaultValue: 'EMAIL',
    description:
      'The order in which we will take in the customer’s response. For instance, if you select Name -> Email, we will assume the customer’s first response is a Name and second response an Email.',
  },

  ONBOARDING_RESPONSE_TYPE: {
    path: 'configuration.onboarding.userResponseType',
    defaultValue: 'TEXT',
    description: 'How would you like the customer to reply?',
  },

  ONBOARDING_MESSAGES: {
    path: 'configuration.onboarding.messages',
    defaultValue: [{ subType: 'QUICK_REPLY_REQUEST', subTypeContent: { options: [] } }],
  },

  ONBOARDING_MESSAGE_SUB_TYPE: {
    path: 'configuration.onboarding.messages[0].subType',
    defaultValue: 'QUICK_REPLY_REQUEST',
  },

  QUICK_REPLY_OPTIONS: {
    path: 'configuration.onboarding.messages[0].subTypeContent.options',
    defaultValue: [],
  },

  SUB_TYPE_CONTENT: {
    path: 'configuration.onboarding.messages[0].subTypeContent',
    defaultValue: { options: [] },
  },

  SHOW_QUICK_REPLY_WHEN_CHAT_ENDED: {
    path: 'configuration.onboarding.showQuickReplyButtonsWhenChatIsEnded',
    defaultValue: false,
    options: [
      {
        value: false,
        label: 'Disabled',
      },
      {
        value: true,
        label: 'Enabled',
      },
    ],
  },

  ONGOING_CHAT_AGENT_ENDED: {
    path: 'configuration.ongoingChat.endMessageAgentEnded',
    defaultValue: 'Thanks for chatting <CUSTOMERNAME>! More questions? Chat again to pick up where we left off.',
  },

  ONGOING_CHAT_EXPIRED: {
    path: 'configuration.ongoingChat.endMessageTimeExpired',
    defaultValue: 'Thanks for chatting <CUSTOMERNAME>! More questions? Chat again to pick up where we left off.',
  },

  ONGOING_CHAT_NEW_SESSION_STARTED: {
    path: 'configuration.ongoingChat.endMessageNewSessionStarted',
    defaultValue:
      'We noticed you’re in another active Chat conversation. Send us a message here, and we can continue in this conversation.',
  },

  // Deprecated. Do not use
  ONGOING_CHAT_NO_REPLY: {
    path: 'configuration.ongoingChat.endMessageNoReply',
    defaultValue:
      "We haven't heard from you in a while, <CUSTOMERNAME>. Type another message to pick up where we left off.",
  },

  ONGOING_CHAT_CONSUMER_NO_REPLY: {
    path: 'configuration.ongoingChat.endMessageConsumerNoReply',
    defaultValue:
      "We haven't heard from you in a while, <CUSTOMERNAME>. Type another message to pick up where we left off.",
  },

  ONBOARDING_ENABLED: {
    path: 'configuration.onboarding.enabled',
    defaultValue: false,
    description: (
      <span>
        Onboarding enabled will allow you to get their email and name. Onboarding disabled will allow you to set up
        messages without gathering this information.
      </span>
    ),
  },

  SUBMIT_BUTTON_TITLE: {
    path: 'configuration.ongoingChat.submitButtonTitle',
    defaultValue: 'Send message',
  },

  // STYLES SECTION

  ALIGNMENT: {
    path: 'configuration.alignment',
    defaultValue: 'RIGHT',
    description: `Pick left or right placement for where the ${CHAT_BRAND.name} will show up on your website`,
  },

  POWERED_BY_GLADLY: {
    path: 'configuration.showPoweredByGladly',
    defaultValue: false,
    description: `Show that ${CHAT_BRAND.name} is powered by Gladly to customers. This will only show on the initial screen.`,
    mobileDescription: `Show that ${CHAT_BRAND.name} is powered by Gladly`,
  },

  ENABLE_ATTACHMENTS: {
    path: 'configuration.attachmentsEnabled',
    defaultValue: true,
    description: 'Allow customers to upload file attachments',
    mobileDescription: 'Allow customers to upload file attachments',
  },

  PRIVACY_DISCLAIMER_IS_ACTIVATED: {
    path: 'configuration.privacyDisclaimer.isActivated',
    defaultValue: false,
    description: `The privacy policy disclaimer will appear in the Search and Quick Actions page and ${CHAT_BRAND.name}.`,
    mobileDescription: `Show the privacy policy disclaimer`,
  },

  PRIVACY_DISCLAIMER_BODY: {
    path: 'configuration.privacyDisclaimer.disclaimerBody',
    defaultValue: '',
  },

  BORDER_COLOR: {
    path: 'configuration.border.color',
    defaultValue: '#FFFFFF',
  },

  THEME_COLORS_ALERT: {
    path: 'configuration.theme.colors.alert',
    defaultValue: '#D4240D',
    defaultDescription: 'Applies to the bubble that shows to indicate you have a message from an agent.',
    description: 'Applies to the Chat icon background color when minimized.',
  },

  THEME_COLORS_GREETING_CLOSE_BUTTON: {
    path: 'configuration.theme.colors.greetingCloseButton',
    defaultValue: '#000000',
    description: 'Applies to the close button when the user chooses to ignore the proactive greeting.',
  },

  THEME_COLORS_BRAND_ACCENT: {
    path: 'configuration.theme.colors.brandAccent',
    defaultValue: '#FFFFFF',
    description: 'Applies to the Chat icon background color when minimized.',
  },

  THEME_COLORS_BRAND_PRIMARY: {
    path: 'configuration.theme.colors.brandPrimary',
    defaultValue: '#FFFFFF',
    description: 'Applies to agent chat bubbles',
  },

  THEME_COLORS_BRAND_SECONDARY: {
    path: 'configuration.theme.colors.brandSecondary',
    defaultValue: '#F3F3F3',
    description: 'Applies to customer chat bubbles',
  },

  THEME_COLORS_CHAT_LAUNCHER: {
    path: 'configuration.theme.colors.chatLauncher',
    defaultValue: '#FFFFFF',
    description: `Applies to the ${CHAT_BRAND.name} button color`,
  },

  THEME_COLORS_SUBMIT_ARROW: {
    path: 'configuration.theme.colors.submitArrow',
    defaultValue: '#FFFFFF',
    description: 'Applies to the submit arrow color to send text',
  },

  THEME_DROPSHADOW_ENABLED: {
    path: 'configuration.theme.dropshadowEnabled',
    defaultValue: true,
  },

  Z_INDEX: {
    path: 'configuration.zIndex',
    defaultValue: 2147483000,
    description: 'The stack order of elements, which only gets applied when it is minimized',
  },

  // GOOGLE ANALYTICS CONFIGURATION
  GA_ENABLED: {
    path: 'configuration.analyticsAttribution.gaConfiguration.enabled',
    defaultValue: false,
    description: 'Enable Google Analytics tracking for sales attribution in chat',
  },

  GA_AUTO_DETECT_TRACKING_ID: {
    path: 'configuration.analyticsAttribution.gaConfiguration.autoDetectTrackingId',
    defaultValue: true,
    description:
      'Automatically detect tracking ID from your website. Required when using Google Tag Manager (GTM). Disable only if using GA4 directly and need to specify a custom tracking ID.',
  },

  GA_TRACKING_ID: {
    path: 'configuration.analyticsAttribution.gaConfiguration.gaTrackingId',
    defaultValue: '',
    description:
      'Custom GA4 tracking ID (G-XXXXXXXXXX). Only use this if you are using GA4 directly (not GTM) and want to override the default tag.',
  },
});

export const DEFAULTS_FOR_NEW_CONFIGURATION = {
  ...FIELDS_MAPPER,
  CUSTOM_TEXT_OFFICE_HOURS_MESSAGE: {
    ...FIELDS_MAPPER.CUSTOM_TEXT_OFFICE_HOURS_MESSAGE,
    defaultValue: OFFICE_HOURS_MESSAGE_DEFAULT,
  },
  CUSTOM_TEXT_BUTTON_TEXT: {
    ...FIELDS_MAPPER.CUSTOM_TEXT_BUTTON_TEXT,
    defaultValue: 'Chat with us!',
  },
  MINIMIZED_ICON_BUTTON_TITLE: {
    ...FIELDS_MAPPER.MINIMIZED_ICON_BUTTON_TITLE,
    defaultValue: `Open ${CHAT_BRAND.name}`,
  },
  ONBOARDING_ENABLED: {
    ...FIELDS_MAPPER.ONBOARDING_ENABLED,
    defaultValue: true,
  },
  ONBOARDING_MESSAGE_SUB_TYPE: {
    ...FIELDS_MAPPER.ONBOARDING_MESSAGE_SUB_TYPE,
    defaultValue: 'QUICK_REPLY_REQUEST',
  },
  SUB_TYPE_CONTENT: {
    ...FIELDS_MAPPER.SUB_TYPE_CONTENT,
    defaultValue: {
      options: [
        { buttonId: '', label: '', id: '1' },
        { buttonId: '', label: '', id: '2' },
        { buttonId: '', label: '', id: '3' },
      ],
    },
  },
  ONBOARDING_MESSAGES: {
    ...FIELDS_MAPPER.ONBOARDING_MESSAGES,
    defaultValue: [{ subType: 'QUICK_REPLY_REQUEST', subTypeContent: {} }],
  },
  BORDER_COLOR: {
    ...FIELDS_MAPPER.BORDER_COLOR,
    defaultValue: '#039b00',
  },
  THEME_COLORS_BRAND_PRIMARY: {
    ...FIELDS_MAPPER.THEME_COLORS_BRAND_PRIMARY,
    defaultValue: '#f3f3f3',
  },
  THEME_COLORS_BRAND_SECONDARY: {
    ...FIELDS_MAPPER.THEME_COLORS_BRAND_SECONDARY,
    defaultValue: '#039b00',
  },
  THEME_COLORS_CHAT_LAUNCHER: {
    ...FIELDS_MAPPER.THEME_COLORS_CHAT_LAUNCHER,
    defaultValue: '#039b00',
  },
  THEME_COLORS_SUBMIT_ARROW: {
    ...FIELDS_MAPPER.THEME_COLORS_SUBMIT_ARROW,
    defaultValue: '#039b00',
  },
  ENABLE_ATTACHMENTS: {
    ...FIELDS_MAPPER.ENABLE_ATTACHMENTS,
    defaultValue: true,
  },
  POWERED_BY_GLADLY: {
    ...FIELDS_MAPPER.POWERED_BY_GLADLY,
    defaultValue: true,
  },
};

export const WORKING_HOURS_SECTION_DESCRIPTION = (
  <span>
    Select time ranges when the {CHAT_BRAND.name} will appear on your
    <br />
    site. Select the correct timezone.
  </span>
);

export const HOVER_BLOCK_INPUTS = [
  {
    attr: FIELDS_MAPPER.CLOSE_BUTTON_TITLE.path,
    label: 'Close icon hover text',
  },
  {
    attr: FIELDS_MAPPER.SUBMIT_BUTTON_TITLE.path,
    label: 'Send icon hover text',
  },
  {
    attr: FIELDS_MAPPER.MINIMIZED_ICON_BUTTON_TITLE.path,
    label: 'Minimized Chat icon hover text',
  },
  {
    attr: FIELDS_MAPPER.ATTACHMENT_ICON_BUTTON_TITLE.path,
    label: 'Attachment icon hover text',
  },
];

export const END_CHAT_BLOCK_INPUTS = [
  {
    attr: FIELDS_MAPPER.ONGOING_CHAT_AGENT_ENDED.path,
    label: 'Chat ended by Agent or Rule',
    description: 'When an Agent or Rule ends the Chat.',
  },
  {
    attr: FIELDS_MAPPER.ONGOING_CHAT_EXPIRED.path,
    label: 'Chat ended by expiration',
    description: (
      <span>
        When the customer fails to respond within a threshold time. Learn how to adjust the time through{' '}
        <ExternalLink to="https://help.gladly.com/docs/configure-messaging-routing-settings#same-agent-assignment-time-limit">
          Same agent assignment time limit
        </ExternalLink>
        .
      </span>
    ),
  },
  {
    attr: FIELDS_MAPPER.ONGOING_CHAT_CONSUMER_NO_REPLY.path,
    label: 'Chat ended by no reply',
    description: 'When the customer doesn’t reply',
  },
  {
    attr: FIELDS_MAPPER.ONGOING_CHAT_NEW_SESSION_STARTED.path,
    label: 'Chat ended by a new active chat ',
    description: 'When the same customer starts a new chat on another device or browser',
  },
];

export const GREETING_DISABLED_ON_BOARDING_BLOCK_INPUTS = [
  {
    attr: FIELDS_MAPPER.CUSTOM_TEXT_INTERACTIVE_ONBOARDING_EMAIL_PROMPT.path,
    label: 'First message',
  },
];

export const GREETING_ENABLED_ON_BOARDING_BLOCK_INPUTS = [
  {
    attr: FIELDS_MAPPER.CUSTOM_TEXT_INTERACTIVE_ONBOARDING_EMAIL_PROMPT.path,
    label: 'First message',
  },
  {
    attr: FIELDS_MAPPER.CUSTOM_TEXT_INTERACTIVE_ONBOARDING_NAME_PROMPT.path,
    label: 'Second message',
  },
  {
    attr: FIELDS_MAPPER.CUSTOM_TEXT_THIRD_MESSAGE.path,
    label: 'Third message',
  },
];

export const SELF_SERVICE_BLOCK_INPUTS = [
  {
    attr: FIELDS_MAPPER.CUSTOM_TEXT_HEADER_TITLE.path,
    label: 'Header',
    description: `Persistent text that remains at the top of the screen upon launching ${CHAT_BRAND.name} and searching answers`,
  },
  {
    attr: FIELDS_MAPPER.CUSTOM_TEXT_HEADER_BACK_BUTTON.path,
    label: 'Back button text',
    description: `Text that lets customers return to the Self-Service page from ${CHAT_BRAND.name}.`,
  },
  {
    attr: FIELDS_MAPPER.CUSTOM_TEXT_SEARCH_PLACEHOLDER.path,
    label: 'Search placeholder text',
    description: 'Text to suggest to customers what to search for',
  },
  {
    attr: FIELDS_MAPPER.CUSTOM_TEXT_HEADER_GREETING.path,
    label: 'Active chat header',
    description: 'Text on the Chat header.',
  },
  {
    attr: FIELDS_MAPPER.CUSTOM_TEXT_BUTTON_TEXT.path,
    label: 'Chat button text',
    description: 'Text that describes the primary action that customers can take (e.g., Chat with us!)',
  },
  {
    attr: FIELDS_MAPPER.CUSTOM_TEXT_INACTIVE_BUTTON.path,
    label: 'Chat input button when unavailable',
    description: 'Text that appears at the bottom of the screen when agents are unavailable to live chat',
  },
  {
    attr: FIELDS_MAPPER.CUSTOM_TEXT_COMPOSITION_MESSAGE_PROMPT.path,
    label: 'Chat placeholder text',
    description: 'Text that suggests what customers can type (e.g., Send message or Enter a message).',
  },
];

export const MOBILE_SELF_SERVICE_BLOCK_INPUTS = [
  {
    attr: FIELDS_MAPPER.CUSTOM_TEXT_COMPOSITION_MESSAGE_PROMPT.path,
    label: 'Chat placeholder text',
    description: 'Text that suggests what customers can type (e.g., Send message or Enter a message).',
  },
];
export const OFFICE_HOURS_BLOCK_INPUTS = [
  {
    attr: FIELDS_MAPPER.CUSTOM_TEXT_OFFICE_HOURS_MESSAGE.path,
    label: '',
    description: FIELDS_MAPPER.CUSTOM_TEXT_OFFICE_HOURS_MESSAGE.description,
    mobileDescription: FIELDS_MAPPER.CUSTOM_TEXT_OFFICE_HOURS_MESSAGE.mobileDescription,
  },
];

export const MOBILE_OFFICE_HOURS_BLOCK_INPUTS = [
  {
    attr: FIELDS_MAPPER.CUSTOM_TEXT_OFFICE_HOURS_MESSAGE.path,
    label: '',
    description: FIELDS_MAPPER.CUSTOM_TEXT_OFFICE_HOURS_MESSAGE.mobileDescription,
  },
];

export const FONT_BLOCK_INPUTS = [
  {
    attr: FIELDS_MAPPER.FONT_LIBRARY_PRIMARY_FONT_DEFAULT.path,
    label: 'Font 1',
    description: 'Applied to the header text and the chat launcher button',
  },
  {
    attr: FIELDS_MAPPER.FONT_LIBRARY_SECONDARY_FONT_DEFAULT.path,
    label: 'Font 2',
    description: 'Applied to the Quick Actions, chat bubble, and input texts.',
  },
];

export const LABELS_BLOCK_INPUTS = [
  {
    attr: FIELDS_MAPPER.CUSTOM_TEXT_HEADER_GREETING.path,
    label: 'Active chat header',
    description: 'Text that shows up at the top when customers begin chatting',
    placeholder: 'Let’s chat',
  },
  {
    attr: FIELDS_MAPPER.CUSTOM_TEXT_COMPOSITION_MESSAGE_PROMPT.path,
    label: 'Chat input placeholder',
    description: 'Text that shows up at the top when customers begin chatting',
    placeholder: 'Send message',
  },
];

export const DISPLAY_ALIGNMENT_BLOCK_INPUTS = [
  {
    attr: FIELDS_MAPPER.Z_INDEX.path,
    label: 'Z-index (optional)',
    description: FIELDS_MAPPER.Z_INDEX.description,
  },
];

export const DROPDOWN_SHADOW_AND_BORDER_BLOCK_INPUTS = [
  {
    attr: FIELDS_MAPPER.BORDER_COLOR.path,
    label: 'Border color',
  },
];

export const COLORS_BLOCK_INPUTS = [
  {
    attr: FIELDS_MAPPER.THEME_COLORS_BRAND_PRIMARY.path,
    label: 'Agent chat bubbles',
    description: FIELDS_MAPPER.THEME_COLORS_BRAND_PRIMARY.description,
  },
  {
    attr: FIELDS_MAPPER.THEME_COLORS_BRAND_SECONDARY.path,
    label: 'Consumer chat bubbles',
    description: FIELDS_MAPPER.THEME_COLORS_BRAND_SECONDARY.description,
  },
];

export const ALERT_COLOR_BLOCK_INPUTS = [
  {
    attr: FIELDS_MAPPER.THEME_COLORS_CHAT_LAUNCHER.path,
    label: `${CHAT_BRAND.name} Button`,
    description: FIELDS_MAPPER.THEME_COLORS_CHAT_LAUNCHER.description,
    svgImage: HelpAppLauncher,
  },
  {
    attr: FIELDS_MAPPER.THEME_COLORS_ALERT.path,
    label: 'Alert color',
    description: FIELDS_MAPPER.THEME_COLORS_ALERT.description,
    svgImage: AlertSvg,
  },
  {
    attr: FIELDS_MAPPER.THEME_COLORS_GREETING_CLOSE_BUTTON.path,
    label: 'Close Button for Proactive Chat',
    description: FIELDS_MAPPER.THEME_COLORS_GREETING_CLOSE_BUTTON.description,
    svgImage: ProactiveGreeting,
  },
  {
    attr: FIELDS_MAPPER.THEME_COLORS_BRAND_ACCENT.path,
    label: 'Minimized chat icon color',
    description: FIELDS_MAPPER.THEME_COLORS_BRAND_ACCENT.description,
    svgImage: MinimizedChat,
  },
  {
    attr: FIELDS_MAPPER.THEME_COLORS_SUBMIT_ARROW.path,
    label: 'Submit arrow color',
    description: FIELDS_MAPPER.THEME_COLORS_SUBMIT_ARROW.description,
    svgImage: SubmitArrow,
  },
];
export const MOBILE_ALERT_COLOR_BLOCK_INPUTS = [
  {
    attr: FIELDS_MAPPER.THEME_COLORS_SUBMIT_ARROW.path,
    label: 'Submit arrow color',
    description: FIELDS_MAPPER.THEME_COLORS_SUBMIT_ARROW.description,
    svgImage: SubmitArrow,
  },
];

export const WIDGET_ALIGNMENT = Object.freeze({
  LEFT: {
    id: 'LEFT',
    label: 'Left',
  },
  RIGHT: {
    id: 'RIGHT',
    label: 'Right',
  },
});

export const BORDER_SIZE = Object.freeze({
  0: {
    id: '0',
    label: 'None',
  },
  '1px': {
    id: '1px',
    label: '1px',
  },
  '2px': {
    id: '2px',
    label: '2px',
  },
  '3px': {
    id: '3px',
    label: '3px',
  },
});

export const BORDER_RADIUS = Object.freeze({
  NONE: { id: 'NONE', label: 'None' },
  SMALL: { id: 'SMALL', label: 'Small' },
  LARGE: { id: 'LARGE', label: 'Large' },
});

export const ONBOARDING_TYPES = Object.freeze({
  DISABLED: {
    id: false,
    label: 'Disabled',
  },
  ENABLED: {
    id: true,
    label: 'Enabled',
  },
});

export const ONBOARDING_RESPONSE_TYPES = Object.freeze({
  TEXT: {
    id: 'TEXT',
    label: 'Type A Message',
  },
  QUICK_REPLY_RESPONSE: {
    id: 'QUICK_REPLY_RESPONSE',
    label: 'Quick Reply Button',
  },
});

export const ONBOARDING_PROMPT_ORDER = Object.freeze({
  EMAIL: {
    id: 'EMAIL',
    label: 'EMAIL, NAME',
  },
  NAME: {
    id: 'NAME',
    label: 'NAME, EMAIL',
  },
});

export const ON_OR_OFF = Object.freeze({
  ON: {
    id: true,
    label: 'On',
  },
  OFF: {
    id: false,
    label: 'Off',
  },
});

export const CONSUMER_NO_REPLY = FIELDS_MAPPER.ONGOING_CHAT_CONSUMER_NO_REPLY;
export const DEPCRATED_NO_REPLY = FIELDS_MAPPER.ONGOING_CHAT_NO_REPLY;
